import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Alert,
  Box,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import axios from "axios";
import { API_ENDPOINT } from "../../../constants/constants";
import { getDbHeader } from "../../../utils2024/utils";
import PropTypes from "prop-types";

const CategorySelectorAccordions = ({
  title = "",
  onSelectionChange,
  selectedCategories: propSelectedCategories = null,
  isOpen = false,
}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [internalSelectedCategories, setInternalSelectedCategories] = useState(
    []
  );
  const abortControllerRef = useRef(null);

  // Determine if component is controlled
  const isControlled = propSelectedCategories !== null;
  // Use prop or internal state based on control mode
  const selectedCategories = isControlled
    ? propSelectedCategories
    : internalSelectedCategories;

  useEffect(() => {
    // Only fetch data when modal is open
    if (!isOpen) return;

    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await axios.get(
          `${API_ENDPOINT}event/get-product-categories`,
          {
            ...getDbHeader(),
            signal,
          }
        );
        setData(response.data);
      } catch (err) {
        if (!axios.isCancel(err)) {
          console.error("Fetch error:", err);
          setError("Failed to load categories");
        }
      } finally {
        if (signal.aborted) {
          console.log("Request was aborted");
        } else {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [isOpen]);

  const handleSelectionUpdate = useCallback(
    (category, isChecked) => {
      const prev = isControlled
        ? propSelectedCategories
        : internalSelectedCategories;
      if (!Array.isArray(prev)) {
        console.error("Previous selections is not an array:", prev);
        return;
      }

      const exists = prev.some(
        (sel) =>
          sel.MainCategory === category.MainCategory &&
          (sel.Category ?? null) === (category.Category ?? null) &&
          (sel.SubCategory ?? null) === (category.SubCategory ?? null)
      );

      const newSelections = exists
        ? prev.filter(
            (sel) =>
              !(
                sel.MainCategory === category.MainCategory &&
                (sel.Category ?? null) === (category.Category ?? null) &&
                (sel.SubCategory ?? null) === (category.SubCategory ?? null)
              )
          )
        : [...prev, category];

      if (!isControlled) {
        setInternalSelectedCategories(newSelections);
      }
      onSelectionChange?.(newSelections);
    },
    [isControlled, propSelectedCategories, onSelectionChange]
  );

  const isCategorySelected = useCallback(
    (category) => {
      return selectedCategories.some(
        (sel) =>
          sel.MainCategory === category.MainCategory &&
          (sel.Category ?? null) === (category.Category ?? null) &&
          (sel.SubCategory ?? null) === (category.SubCategory ?? null)
      );
    },
    [selectedCategories]
  );

  const handleCheckboxChange = useCallback(
    (category) => {
      const isChecked = isCategorySelected(category);
      handleSelectionUpdate(category, !isChecked);
    },
    [handleSelectionUpdate, isCategorySelected]
  );

  const { mainGroups, categoryGroups } = useMemo(() => {
    if (!data?.categories) return { mainGroups: {}, categoryGroups: {} };

    const groups = data.categories.reduce((acc, cat) => {
      const main = cat.MainCategory || "Unknown";
      if (!acc[main]) acc[main] = [];
      acc[main].push(cat);
      return acc;
    }, {});

    const catGroups =
      data.type === "triple"
        ? data.categories.reduce((acc, item) => {
            const key = `${item.MainCategory}-${item.Category}`;
            if (!acc[key]) acc[key] = [];
            acc[key].push(item);
            return acc;
          }, {})
        : {};

    return { mainGroups: groups, categoryGroups: catGroups };
  }, [data]);

  const renderSingle = (mainCategory) => {
    const categoryObj = {
      MainCategory: mainCategory,
      Category: null,
      SubCategory: null,
    };

    return (
      <FormControlLabel
        control={
          <Checkbox
            checked={isCategorySelected(categoryObj)}
            onChange={() => handleCheckboxChange(categoryObj)}
          />
        }
        label={<Typography>{mainCategory}</Typography>}
      />
    );
  };

  const renderDouble = (items) =>
    items.map((cat, idx) => (
      <FormControlLabel
        sx={{ width: "100%" }}
        key={`${cat.MainCategory}-${cat.Category}-${idx}`}
        control={
          <Checkbox
            checked={isCategorySelected(cat)}
            onChange={() => handleCheckboxChange(cat)}
          />
        }
        label={<Typography variant="body2">{cat.Category || "N/A"}</Typography>}
      />
    ));

  const renderTriple = (mainCategory) =>
    Object.entries(categoryGroups)
      .filter(([key]) => key.startsWith(mainCategory))
      .map(([key, items]) => (
        <Box key={key} mb={2}>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
            {key.split("-")[1]}
          </Typography>
          {items.map((subItem, subIdx) => (
            <FormControlLabel
              sx={{ width: "100%" }}
              key={`${key}-${subIdx}`}
              control={
                <Checkbox
                  checked={isCategorySelected(subItem)}
                  onChange={() => handleCheckboxChange(subItem)}
                />
              }
              label={
                <Typography variant="body2">
                  {subItem.SubCategory || "N/A"}
                </Typography>
              }
            />
          ))}
        </Box>
      ));

  if (!isOpen) return null;

  if (loading) {
    return (
      <Box textAlign="center" mt={4}>
        <CircularProgress />
        <Typography variant="body2" mt={1}>
          Loading categories...
        </Typography>
      </Box>
    );
  }

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data?.categories?.length)
    return <Alert severity="info">No categories available</Alert>;

  return (
    <Box my={2}>
      {title && (
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          {title}
        </Typography>
      )}

      {Object.entries(mainGroups).map(([mainCategory, items]) => (
        <Accordion key={`${mainCategory}-${data.type}`}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" fontWeight="bold">
              {mainCategory}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {data.type === "single" && renderSingle(mainCategory)}
            {data.type === "double" && renderDouble(items)}
            {data.type === "triple" && renderTriple(mainCategory)}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};
CategorySelectorAccordions.propTypes = {
  title: PropTypes.string,
  onSelectionChange: PropTypes.func,
  selectedCategories: PropTypes.array,
  isOpen: PropTypes.bool,
};

export default React.memo(CategorySelectorAccordions);
